﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A component to display a "card" with information about a user.

import { applyThemeStyles } from "~base/Styling";
import * as React from "react";
import { createUseStyles } from "react-jss";
import { JssClass } from "~base/Styling";
import { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import { useIntl } from "~base/Internationalization/IntlContext";
import { useNavigateService } from "~base/Services/NavigateService";
import { LegacyIcon } from "~base/Components/Primitives/LegacyIcon";
import IconButton from "~base/Components/Primitives/IconButton";
import Grid from "~base/Components/Layout/Grid";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface UserInfoProps {
    className?: string;
    displayName: string;
    role: string;
    editLink?: string;
    customization?: UserInfoCustomizations;
}

export interface UserInfoCustomizations {
    pictureFrame?: React.ReactNode;
}

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

export interface ConsilioThemePagesSharedUserInfoStyles {
    userInfo?: JssClass;
    displayName?: JssClass;
    editButton?: JssClass;
    loggedInAs?: JssClass;
    role?: JssClass;
}

const useStyles = createUseStyles(
    (theme: ConsilioBaseTheme) => ({
        userInfo: applyThemeStyles(
            theme.components.pages.shared.userInfo.userInfo,
            {
                fontSize: "13px",
                lineHeight: "16px",
                fontFamily: theme.constants.defaultFont,
                listStyle: "none",
                paddingInlineStart: "16px",
                textAlign: "left",
            }
        ),
        loggedInAs: applyThemeStyles(
            theme.components.pages.shared.userInfo.loggedInAs,
            {
                opacity: 0.5,
            }
        ),
        displayName: applyThemeStyles(
            theme.components.pages.shared.userInfo.displayName,
            {
                fontWeight: "bold",
            }
        ),
        role: applyThemeStyles(theme.components.pages.shared.userInfo.role, {}),
        genericUser: {
            position: "relative",
            width: "66px",
            height: "66px",
            fontSize: "66px",
            color: "#ECECEC",
        },
        genericUserOverlay: {
            fontSize: "66px",
            color: "#BABABA",
        },
        editButton: applyThemeStyles(
            theme.components.pages.shared.userInfo.editButton,
            {
                fontSize: "24px",
                margin: "-10px 0px 0px 0px",
            }
        ),
    }),
    { name: "UserInfoCard" }
);

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

const UserInfoCard: React.FunctionComponent<UserInfoProps> = ({
    editLink,
    displayName,
    role,
    className,
    customization,
}) => {
    const intl = useIntl();
    const classes = useStyles();
    const navigate = useNavigateService();

    const customizedPictureFrame = customization && customization.pictureFrame;
    const pictureFrame = customizedPictureFrame ? (
        customizedPictureFrame
    ) : (
        <LegacyIcon
            icon="Consilio.UI.Icons.GenericUser"
            className={classes.genericUser}
            overlayClassName={classes.genericUserOverlay}
        />
    );

    return (
        <Grid templateColumns="auto 1fr" className={className}>
            {pictureFrame}
            <ul className={classes.userInfo}>
                <li className={classes.loggedInAs}>
                    {intl.formatText("VisiFinance.UI.Start.InloggadSom")}
                </li>
                <li className={classes.displayName}>
                    {displayName}
                    &nbsp;
                    {editLink && (
                        <IconButton
                            buttonClassName={classes.editButton}
                            styleType={"Transparent"}
                            icon="Consilio.UI.Icons.Pen"
                            onClick={() => {
                                navigate.current(editLink);
                            }}
                        />
                    )}
                </li>
                <li className={classes.role}>{intl.tryFormatText(role)}</li>
            </ul>
        </Grid>
    );
};

export default UserInfoCard;
export { UserInfoCard };
